import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { Fade } from "@progress/kendo-react-animation";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { TimePicker } from "@progress/kendo-react-dateinputs";
import { Dialog } from "@progress/kendo-react-dialogs";
import { useLocalization } from "@progress/kendo-react-intl";
import { Avatar } from "@progress/kendo-react-layout";
import { Notification, NotificationGroup } from "@progress/kendo-react-notification";
import { plusIcon, saveIcon, trashIcon } from "@progress/kendo-svg-icons";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { AccessToken } from "../../../config/AccessToken";
import { loadingPanel } from "../../LoadingPanel";
import { useNavigate } from "react-router-dom";

/**
 * ConfigRulesTimeslot is a React functional component that manages the creation,
 * updating, and deletion of time slots. It provides a user interface for adding 
 * new time slots, editing existing ones, and removing them. The component uses 
 * various state hooks to manage form inputs and the list of time slots. It also 
 * handles authentication and authorization for API requests to the server, 
 * providing notifications for success and error states during these operations.
 */

export const ConfigRulesTimeslot = () => {

    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const COLORS = {
        primary: '#1A8893',
        panelBg: '#F7F7F7',
        selectedBg: '#198792',
        white: '#ffffff',
        primaryDark: '#1A8893',
        borderStrong: 'rgba(26, 136, 147, 0.28)',
        surface: '#EAF6F7'
    };
    const [instance, accessTokenRequest] = AccessToken();

    const languageId = localStorage.getItem('languageId');
    const isEnglishLanguage = languageId === 'en-US';
    const localizationService = useLocalization();

    const userType = localStorage.getItem('userType');

    const [state, setState] = useState<any>({
        id: 0,
        startDate: null,
        endDate: null
    });

    const [selectedStartDate, setSelectedStartDate] = useState(new Date());
    const [selectedEndDate, setSelectedEndDate] = useState(new Date());
    const [timeslotIdSelected, setTimeslotIdSelected] = useState(-1);

    const [newTimeSlot, setNewTimeSlot] = useState<any>([]);
    const [timeslotPendingDelete, setTimeslotPendingDelete] = useState<{ id: any; index: any } | null>(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<any>('');

    const withAccessToken = async (operation: (accessToken: string) => Promise<void> | void) => {
        const storedAccessToken = localStorage.getItem('accessToken');
        if (storedAccessToken) {
            await operation(storedAccessToken);
            return;
        }

        try {
            await instance.initialize();
            const accessTokenResponse: any = await instance.acquireTokenSilent(accessTokenRequest);
            await operation(accessTokenResponse.accessToken);
        } catch (error: any) {
            if (error instanceof InteractionRequiredAuthError) {
                instance.acquireTokenRedirect(accessTokenRequest);
            }
            console.log(error);
        }
    };

    const formatToHHmm = (dateInput: Date | string) => {
        const date = new Date(dateInput);
        const hour = (date.getHours() < 10 ? '0' : '') + date.getHours();
        const minutes = (date.getMinutes() < 10 ? '0' : '') + date.getMinutes();
        return `${hour}:${minutes}`;
    };

    const toIsoWithoutTimezone = (date: Date) => {
        return new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString();
    };

    const navigate = useNavigate()

    useEffect(() => {
        loadTimeslot();
    }, []);

    const hasAppliedDefaultSelectionRef = useRef(false);

    useEffect(() => {
        if (hasAppliedDefaultSelectionRef.current) {
            return;
        }

        if (timeslotIdSelected !== -1) {
            hasAppliedDefaultSelectionRef.current = true;
            return;
        }

        if (!Array.isArray(newTimeSlot) || newTimeSlot.length === 0) {
            return;
        }

        hasAppliedDefaultSelectionRef.current = true;
        loadSelectedTimeslot(newTimeSlot[0]);
    }, [newTimeSlot, timeslotIdSelected]);

    const convertDateTime = (dateTime: any) => {

        if (dateTime == null) {
            return null
        }

        const [datePart, timePart] = dateTime?.split(' ');
        const [day, month, year] = datePart?.split('/');
        const [hour, minute, seconds] = timePart?.split(':');

        const date = new Date(Date.UTC(year, month - 1, day, hour, minute, seconds))

        return date
    }

    const formatDateFromIsoToString = (date: any) => {
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
            return '';
        }

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');

        if (languageId == 'en-US') {
            hours = hours % 12;
            hours = hours === 0 ? 12 : hours; // convert 0 → 12
            const hours12 = String(hours).padStart(2, '0');
            return `${hours12}:${minutes}`;
        }
        else {
            const hours24 = String(hours).padStart(2, '0');
            return `${hours24}:${minutes}`;
        }
    }

    const toTimePickerDate = (value: any, fallback: Date = new Date()) => {
        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            return value;
        }

        if (typeof value === 'string') {
            const trimmed = value.trim();
            const timeMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);

            if (timeMatch) {
                const [, hours, minutes, seconds = '0'] = timeMatch;
                const date = new Date(fallback);
                date.setHours(Number(hours), Number(minutes), Number(seconds), 0);
                return date;
            }

            const parsed = new Date(trimmed);
            if (!Number.isNaN(parsed.getTime())) {
                return parsed;
            }
        }

        return fallback;
    };

    const loadTimeslot = async () => {

        setLoading(true);

        const commonOperations = async (accessToken: string) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/timeslot/list`, { headers })
                .then((res) => {
                    const data: any = res?.data?.data
                    const timeslotList = (data ?? []).map((timeslot: any) => ({
                        ...timeslot,
                        startDate: formatDateFromIsoToString(convertDateTime(timeslot?.startDate)),
                        endDate: formatDateFromIsoToString(convertDateTime(timeslot?.endDate))
                    }));

                    setNewTimeSlot(timeslotList);

                    navigate(`/home/configuration/rules/timeslot`);

                    setNewTimeSlot(timeslotList);
                });
        };

        await withAccessToken(commonOperations);
        setLoading(false);
    }

    const handleChange = (event: any) => {
        let name = event.target.name || event.target.element.current.name;
        const rawValue = event?.value ?? event?.target?.value;
        let currentValue = toTimePickerDate(rawValue, name === 'endDate' ? selectedEndDate : selectedStartDate);

        setState({
            ...state,
            [name]: currentValue,
        });

        if (name == 'startDate') {
            const hour = String(currentValue.getHours()).padStart(2, '0');
            const minutes = String(currentValue.getMinutes()).padStart(2, '0');
            state.startDate = `${hour}:${minutes}`
            setSelectedStartDate(currentValue)
        }
        if (name == 'endDate') {
            const hour = String(currentValue.getHours()).padStart(2, '0');
            const minutes = String(currentValue.getMinutes()).padStart(2, '0');
            state.endDate = `${hour}:${minutes}`
            setSelectedEndDate(currentValue)
        }
    };

    const [saved, setSaved] = useState(false);
    const [removed, setRemoved] = useState(false);

    const toUtcIsoString = (value: Date | null | undefined) => {
        if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
            return '';
        }

        return value.toISOString().slice(0, 16) + 'Z';
    };

    const saveTimeslot = async () => {

        setLoading(true);

        const startDate = toUtcIsoString(selectedStartDate);
        const endDate = toUtcIsoString(selectedEndDate);


        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.post(`${SERVER_URL}/timeslot?startDate=${startDate}&endDate=${endDate}`, null, { headers })
                .then(() => {
                    let startDateFormatted: any = ''
                    let endDateFormatted: any = ''
                    let newTimeSlotFormatted: any = []
                    if (state?.startDate != null) {
                        const startDate = new Date(state?.startDate);
                        const hour = (startDate.getHours() < 10 ? '0' : '') + startDate.getHours();
                        const minutes = (startDate.getMinutes() < 10 ? '0' : '') + startDate.getMinutes();
                        startDateFormatted = `${hour}:${minutes}`
                    }
                    if (state?.endDate != null) {
                        const endDate = new Date(state?.endDate);
                        const hour = (endDate.getHours() < 10 ? '0' : '') + endDate.getHours();
                        const minutes = (endDate.getMinutes() < 10 ? '0' : '') + endDate.getMinutes();
                        endDateFormatted = `${hour}:${minutes}`
                    }
                    newTimeSlotFormatted = {
                        startDate: startDate,
                        endDate: endDateFormatted
                    }

                    setState((prev: any) => ({
                        ...prev,
                        startDate: null,
                        endDate: null
                    }));

                    setNewTimeSlot((prev: any) => [...prev, newTimeSlotFormatted]);
                    setSaved(true);

                    setTimeout(() => {
                        setSaved(false)
                    }, 3000);
                }).catch((data) => {
                    setError(data?.message);
                    setLoading(false);
                });
        }

        await withAccessToken(commonOperations);

    }

    const updatedTimeslotSelected = async (e: any, timeslotId: any) => {
        e.preventDefault()

        if (!selectedStartDate || !selectedEndDate) {
            return;
        }

        setLoading(true);
        const startDateChanged = toUtcIsoString(selectedStartDate);
        const endDateChanged = toUtcIsoString(selectedEndDate);
        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            try {
                await axios.put(`${SERVER_URL}/timeslot/${timeslotId}?startDate=${startDateChanged}&endDate=${endDateChanged}`, null, { headers })
                setSaved(true)

                loadTimeslot()
                setLoading(false);
            } catch (error: any) {
                setError(error?.message);
            } finally {
                setLoading(false);
            }
        }

        await withAccessToken(commonOperations);

    }

    const removeTimeslot = async (timeslotId: any, index: any) => {

        setLoading(true);

        const commonOperations = async (accessToken: string) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            try {
                await axios.delete(`${SERVER_URL}/timeslot/${timeslotId}`, { headers });
                setNewTimeSlot((oldValues: any) => oldValues.filter((_: any, i: any) => i !== index));
                setRemoved(true);
                setTimeout(() => {
                    setRemoved(false);
                }, 3000);
            } catch (data: any) {
                if (data?.response?.data?.errorMessage === "Error executing the API: Error: Timeslot is connected to the subscription") {
                    if (languageId === 'it-IT') {
                        setError("Errore: Il timeslot eÌ€ connesso alla sottoscrizione");
                    } else {
                        setError("Error: Timeslot is connected to the subscription");
                    }
                } else {
                    setError(data?.message ?? 'Unexpected error while removing timeslot');
                }

                setTimeout(() => {
                    setError('');
                }, 5000);
            } finally {
                setLoading(false);
            }
        }

        await withAccessToken(commonOperations);

    }

    const openRemoveTimeslotDialog = (timeslotId: any, index: any) => {
        setTimeslotPendingDelete({ id: timeslotId, index });
    }

    const closeRemoveTimeslotDialog = () => {
        setTimeslotPendingDelete(null);
    }

    const confirmRemoveTimeslot = async () => {
        if (!timeslotPendingDelete) {
            return;
        }

        await removeTimeslot(timeslotPendingDelete.id, timeslotPendingDelete.index);
        closeRemoveTimeslotDialog();
    }

    const loadSelectedTimeslot = (timeslot: any) => {

        let startDateConverted: any;
        let endDateConverted: any;
        let idTimeslot: any = timeslot.id;


        if (timeslot?.startDate != null) {
            let pieces: any = timeslot.startDate.split(':')
            let hours, minutes;
            if (pieces.length === 2) {
                hours = parseInt(pieces[0], 10);
                minutes = parseInt(pieces[1], 10);
            }
            startDateConverted = new Date();
            startDateConverted.setHours(hours)
            startDateConverted.setMinutes(minutes)
            setSelectedStartDate(startDateConverted);
        }
        if (timeslot?.endDate != null) {
            let pieces: any = timeslot.endDate.split(':')
            let hours: any, minutes: any;
            if (pieces.length === 2) {
                hours = parseInt(pieces[0], 10);
                minutes = parseInt(pieces[1], 10);
            }
            endDateConverted = new Date();
            endDateConverted.setHours(hours)
            endDateConverted.setMinutes(minutes)
            setSelectedEndDate(endDateConverted);
        }
        setTimeslotIdSelected(idTimeslot);
    };

    const itemRefs: any = useRef([]);

    const bolderStyles = `
        .rules-timeslot-root {
            gap: 12px;
            height: 100%;
            padding: 10px;
        }

        .rules-timeslot-left-panel {
            display: flex;
            flex-direction: column;
            padding: 10px;
            background: #F7F7F7;
            flex: 1.5 1 0px;
            overflow-y: hidden;
            border: 1px solid #d6e8ee;
            border-radius: 14px;
            box-shadow: 0 10px 24px rgba(10, 55, 75, 0.08);
        }

        .rules-timeslot-title {
            font-weight: 800;
            letter-spacing: 0.4px;
            text-shadow: 0 1px 0 rgba(255, 255, 255, 0.7);
        }

        .rules-timeslot-item {
            background: #ffffff;
            border: 1px solid #cfe3ea;
            border-radius: 12px;
            padding: 10px;
            box-shadow: 0 3px 10px rgba(10, 55, 75, 0.06);
            transition: transform 120ms ease, box-shadow 180ms ease;
        }

        .rules-timeslot-item:hover {
            transform: translateY(-1px);
            box-shadow: 0 8px 18px rgba(10, 55, 75, 0.12);
        }
    `;

    return (
        <div className="flex-parent-element rules-timeslot-root" style={{ display: 'flex', alignItems: 'stretch' }}>
            <style>{bolderStyles}</style>
            <NotificationGroup
                style={{
                    right: 0,
                    bottom: 50,
                    alignItems: "flex-start",
                    flexWrap: "wrap-reverse",
                }}
            >
                <Fade enter={true} exit={true}>
                    {removed && (
                        <Notification
                            type={{ style: "success", icon: true }}
                            closable={true}
                            onClose={() => setRemoved(false)}
                        >
                            <span>{localizationService.toLanguageString('configuration.rules.timeslot_removed', 'NOT_FOUND')}</span>
                        </Notification>
                    )}
                    {error && (
                        <Notification
                            type={{ style: "error", icon: true }}
                            closable={true}
                            onClose={() => setError('')}
                        >
                            <span>{error}</span>
                        </Notification>
                    )}
                    {/*{created && (
                            <Notification
                                type={{ style: "success", icon: true }}
                                closable={true}
                                onClose={() => setCreated(false)}
                            >
                                <span>{localizationService.toLanguageString('configuration.rules.timeslot_updated', 'NOT_FOUND')}</span>
                            </Notification>
                        )}*/}
                    {saved && (
                        <Notification
                            type={{ style: "success", icon: true }}
                            closable={true}
                            onClose={() => setSaved(false)}
                        >
                            <span>{localizationService.toLanguageString('configuration.rules.timeslot_saved', 'NOT_FOUND')}</span>
                        </Notification>
                    )}
                </Fade>
            </NotificationGroup>

            {timeslotPendingDelete && (
                <Dialog
                    className="confirm-action-dialog"
                    title={localizationService.toLanguageString('configuration.rules.timeslot_remove', 'Remove timeslot')}
                    onClose={closeRemoveTimeslotDialog}
                >
                    <div className="dialog-message">
                        {localizationService.toLanguageString(
                            'configuration.rules.timeslot_remove_confirm',
                            'NOT_FOUND'
                        )}
                    </div>
                    <div className="dialog-actions">
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                            onClick={closeRemoveTimeslotDialog}
                        >
                            {localizationService.toLanguageString('no', 'No')}
                        </button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                            onClick={confirmRemoveTimeslot}
                        >
                            {localizationService.toLanguageString('yes', 'Yes')}
                        </button>
                    </div>
                </Dialog>
            )}

            {/*left panel*/}
            {loading && loadingPanel}
            <div
                className="rules-timeslot-left-panel"
            >
                <div style={{ textAlign: 'center', marginBottom: '20px', justifyContent: 'space-between' }}>
                    <p className="rules-timeslot-title" style={{ display: 'inline-flex', textAlign: 'center', fontSize: '20px', color: COLORS.primaryDark }}>
                        {localizationService.toLanguageString('configuration.rules.timeslot', 'NOT_FOUND')} ({newTimeSlot.length})
                    </p>
                    <Button
                        title={localizationService.toLanguageString('configuration.rules.timeslot_new', 'NOT_FOUND')}
                        themeColor={"primary"}
                        className="plusParam"
                        style={{
                            marginRight: '10px',
                            marginTop: '0px',
                            borderRadius: '10px',
                            //boxShadow: '0 3px 10px rgba(26,136,147,0.28)'
                        }}
                        disabled={userType === 'StandardOnlyLocator'}
                        onClick={() => {
                            let resetTimeslot = -1
                            setTimeslotIdSelected(resetTimeslot);
                            setState((prev: any) => ({ ...prev, startDate: null, endDate: null }));
                        }}
                    >
                        <Avatar
                            className="plusParam"
                        >
                            <SvgIcon
                                icon={plusIcon}
                                style={{ margin: '8px' }}
                            />
                        </Avatar>
                    </Button>
                </div>

                <div
                    className="flex-left-child-element"
                    style={{
                        flex: 1,
                        minHeight: 0,
                        padding: '8px',
                        overflowY: 'auto',
                        width: '100%',
                        height: '85%'
                    }}
                >
                    {newTimeSlot?.map((timeSlot: any, index: any) => {
                        const isSelected = timeSlot.id === timeslotIdSelected;

                        return (
                            <div
                                key={index}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}
                            >
                                <div
                                    className="trash-icon"
                                    style={{
                                        cursor: 'pointer',
                                        alignContent: 'center',
                                        marginRight: '10px',
                                        pointerEvents: `${userType === 'StandardOnlyLocator' ? 'none' : 'auto'}`,
                                        opacity: `${userType === 'StandardOnlyLocator' ? 0.5 : 1}`
                                    }}
                                    title={localizationService.toLanguageString('configuration.rules.timeslot_remove', 'NOT_FOUND')}
                                    onClick={() => openRemoveTimeslotDialog(timeSlot.id, index)}
                                >
                                    <Avatar
                                        className="trash-icon"
                                    >
                                        <SvgIcon
                                            icon={trashIcon}
                                            style={{ verticalAlign: 'baseline', margin: '10px' }}
                                        />
                                    </Avatar>
                                </div>
                                <p
                                    className="rules-timeslot-item"
                                    style={{
                                        cursor: 'pointer',
                                        marginLeft: '0px',
                                        marginBottom: '0px',
                                        flex: 1,
                                        fontSize: `${timeSlot.name != '' ? 'clamp(10px, 0.8vw, 16px)' : 'clamp(9px, 0.7vw, 16px)'}`,
                                        color: isSelected ? COLORS.white : COLORS.primaryDark,
                                        background: isSelected ? COLORS.selectedBg : COLORS.white
                                    }}
                                    onClick={() => loadSelectedTimeslot(timeSlot)}
                                >
                                    {timeSlot.startDate + ' - ' + timeSlot.endDate}
                                </p>
                            </div>
                        )
                    })}
                </div>

            </div>

            {/*right panel*/}
            <div
                className="flex-right-child-element"
                style={{
                    background: COLORS.white,
                    flexGrow: '2',
                    minWidth: '320px',
                    borderRadius: '12px',
                    border: '1px solid #cfe3ea',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                    height: '20%',
                    alignContent: 'center',
                }}
            >
                <form>
                    <fieldset>
                        <div style={{ display: 'flex', gap: '12px', maxWidth: '380px' }}>
                            <div className="k-form-field" style={{ display: 'flex', alignItems: 'center' }}>
                                <p style={{ display: 'inline-block', width: '50px', margin: 0, fontWeight: 700, color: COLORS.primaryDark }}>{localizationService.toLanguageString('configuration.rules.timeslot_from', 'NOT_FOUND')}</p>
                                <TimePicker
                                    format={isEnglishLanguage ? "hh:mm a" : "HH:mm"}
                                    name="startDate"
                                    validityStyles={false}
                                    value={selectedStartDate}
                                    width={150}
                                    required={true}
                                    onChange={handleChange}
                                    validationMessage={
                                        selectedStartDate === null
                                            ? `${localizationService.toLanguageString('configuration.rules.timeslot_from_is_required', 'NOT_FOUND')}`
                                            : ""
                                    }
                                    disabled={userType === 'StandardOnlyLocator'}
                                />
                            </div>
                            <div className="k-form-field" style={{ display: 'flex', alignItems: 'center' }}>
                                <p style={{ display: 'inline-block', width: '50px', margin: '0px 0px 0px 30px', fontWeight: 700, color: COLORS.primaryDark }}>{localizationService.toLanguageString('configuration.rules.timeslot_to', 'NOT_FOUND')}</p>
                                <TimePicker
                                    format={isEnglishLanguage ? "hh:mm a" : "HH:mm"}
                                    name="endDate"
                                    validityStyles={false}
                                    value={selectedEndDate}
                                    width={150}
                                    required={true}
                                    onChange={handleChange}
                                    validationMessage={
                                        selectedEndDate === null
                                            ? `${localizationService.toLanguageString('configuration.rules.timeslot_to_is_required', 'NOT_FOUND')}`
                                            : `${localizationService.toLanguageString('configuration.rules.timeslot_to_cannot_be_before_from', 'NOT_FOUND')}`
                                    }
                                    disabled={userType === 'StandardOnlyLocator'}
                                />
                            </div>

                            {timeslotIdSelected === -1 ?
                                <Button
                                    title={localizationService.toLanguageString('configuration.registry.group_save', 'NOT_FOUND')}
                                    themeColor={"primary"}
                                    className="saveParam"
                                    style={{ marginLeft: '20px' }}
                                    disabled={state.startDate == '' || state.endDate == '' || userType === 'StandardOnlyLocator'}
                                    onClick={() => saveTimeslot()}
                                >
                                    <Avatar
                                        className="saveParam"
                                    >
                                        <SvgIcon
                                            icon={saveIcon}
                                            style={{ margin: '8px' }}
                                        />
                                    </Avatar>
                                </Button>
                                :
                                <div style={{ display: 'inline' }}>
                                    <Button
                                        title={localizationService.toLanguageString('configuration.rules.timeslot_update', 'NOT_FOUND')}
                                        themeColor={"primary"}
                                        className="saveParam"
                                        style={{ marginLeft: '20px' }}
                                        disabled={userType === 'StandardOnlyLocator'}
                                        onClick={(e: any) => updatedTimeslotSelected(e, timeslotIdSelected)}
                                    >
                                        <Avatar
                                            className="saveParam"
                                        >
                                            <SvgIcon
                                                icon={saveIcon}
                                                style={{ margin: '8px' }}
                                            />
                                        </Avatar>
                                    </Button>
                                </div>
                            }

                        </div>
                    </fieldset>

                    <br />

                </form>

            </div>
        </div>
    )
}
